import { useRef, useState } from 'react';
import { useMutation, useAction } from 'convex/react';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: component in src/pages/insurance/benefit-plan-form/ requires four levels up to reach convex/_generated/
import type { Id } from '../../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import type { IExtractedBenefitPlan } from '@/@types/claims';

interface IImportPlanDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (data: IExtractedBenefitPlan) => void;
}

export function ImportPlanDialog({ open, onClose, onImport }: IImportPlanDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extracted, setExtracted] = useState<IExtractedBenefitPlan | null>(null);
  const createUploadUrl = useMutation(api.storage.createUploadUrl);
  const extractPlan = useAction(api.insurance.importBenefitPlan.extractBenefitPlanData);

  const handleUpload = async (file: File) => {
    setIsProcessing(true);
    try {
      const uploadUrl = await createUploadUrl();
      const res = await fetch(uploadUrl, { method: 'POST', body: file, headers: { 'Content-Type': file.type } });
      if (!res.ok) throw new Error('Upload failed');
      const { storageId } = (await res.json()) as { storageId: Id<'_storage'> };
      const result = await extractPlan({ storageId });
      setExtracted(result);
    } catch {
      toast.error('Failed to process PDF');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirm = () => {
    if (extracted) { onImport(extracted); setExtracted(null); onClose(); }
  };

  const handleClose = () => { setExtracted(null); onClose(); };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Import from PDF</DialogTitle></DialogHeader>
        {!extracted ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Upload a Schedule of Benefits PDF to extract plan data automatically.</p>
            <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) void handleUpload(f); }} />
            <Button onClick={() => fileInputRef.current?.click()} disabled={isProcessing} className="w-full" variant="outline">
              {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {isProcessing ? 'Processing…' : 'Choose PDF file'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Extracted <strong>{extracted.sections.length}</strong> sections with{' '}
              <strong>{extracted.sections.reduce((n, s) => n + s.items.length, 0)}</strong> items.
            </p>
            <ul className="text-sm space-y-1 max-h-52 overflow-y-auto">
              {extracted.sections.map((sec, i) => (
                <li key={i} className="text-muted-foreground">
                  <span className="font-medium text-foreground">{sec.name}</span> — {sec.items.length} items
                </li>
              ))}
            </ul>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          {extracted && <Button onClick={handleConfirm}>Populate Form</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
